import * as turf from '@turf/turf';
import type { GridCell, CellMetadata } from '../types';
import { generateBlockchainOwnership } from './blockchain';

const GRID_SIZE_KM = 1.0; // 1km x 1km = 100 hectares (TEMPORARY - for performance)

/**
 * Generates a 1-hectare grid system overlay for the Arariboia Reserve
 * Returns ~41,300 cells total for 413,000 hectares
 */
export function generateGrid(
  boundary: [number, number][],
  center: [number, number]
): GridCell[] {
  const cells: GridCell[] = [];

  // Create polygon from boundary
  const polygon = turf.polygon([boundary]);
  const bbox = turf.bbox(polygon);

  // Generate square grid
  const options = {
    units: 'kilometers' as const,
    mask: polygon
  };

  const squareGrid = turf.squareGrid(bbox, GRID_SIZE_KM, options);

  console.log(`Grid generated from bbox: ${squareGrid.features.length} cells (before filtering)`);

  // Convert features to GridCell objects
  // IMPORTANT: Filter only cells whose CENTER point is within the boundary polygon
  squareGrid.features.forEach((feature, index) => {
    const cellCenter = turf.center(feature);
    const coords = cellCenter.geometry.coordinates;

    // Check if cell center is within the boundary polygon
    const isInside = turf.booleanPointInPolygon(cellCenter, polygon);

    if (!isInside) {
      return; // Skip cells outside the boundary
    }

    const bounds = turf.bbox(feature);
    const cellId = `cell-${index}`;
    const cellMetadata = generateCellMetadata(coords as [number, number], center, index);

    // Generate blockchain ownership (10% of cells)
    const ownership = generateBlockchainOwnership(cellId, index);
    const blockchain = ownership ? {
      tokenId: ownership.nftTokenId,
      owner: ownership.owner,
      transactionHash: `0x${index.toString(16).padStart(64, '0')}`,
      timestamp: ownership.purchaseDate,
      nftCertificate: {
        tokenId: ownership.nftTokenId,
        name: `Arariboia Cell #${index}`,
        description: `1-hectare biodiversity conservation cell in the Arariboia Indigenous Reserve. BQI Score: ${cellMetadata.bqiScore}`,
        image: `ipfs://Qm${cellId.slice(-40)}/certificate.png`,
        attributes: [
          { trait_type: 'Area (hectares)', value: 1.0 },
          { trait_type: 'BQI Score', value: cellMetadata.bqiScore },
          { trait_type: 'Species Count', value: cellMetadata.speciesCount },
          { trait_type: 'Carbon Stock (tCO₂)', value: cellMetadata.carbonStock },
          { trait_type: 'Vegetation Coverage (%)', value: cellMetadata.vegetationCoverage },
          { trait_type: 'Purchase Date', value: ownership.purchaseDate.toISOString().split('T')[0] }
        ],
        certificateUrl: `https://arariboia.bio/certificates/${ownership.nftTokenId}`
      },
      snapshots: []
    } : undefined;

    cells.push({
      id: cellId,
      coordinates: [coords[1], coords[0]], // [lat, lng]
      bounds: [
        [bounds[1], bounds[0]], // Southwest
        [bounds[3], bounds[2]]  // Northeast
      ],
      metadata: cellMetadata,
      blockchain
    });
  });

  console.log(`Generated ${cells.length} grid cells (1km² each, 100 hectares) after filtering`);
  console.log(`Expected: ~4,130 cells for 413,000 hectares (at 1km resolution)`);
  return cells;
}

/**
 * Generates realistic biodiversity metadata for a grid cell
 * Uses cell index for deterministic randomness
 */
function generateCellMetadata(
  coords: [number, number],
  center: [number, number],
  index: number
): CellMetadata {
  // Calculate distance from center (affects biodiversity)
  const distance = turf.distance(coords, center, { units: 'kilometers' });

  // Base scores affected by distance from center
  const distanceFactor = Math.max(0, 1 - (distance / 20));

  // Use deterministic randomness based on cell index
  const seededRandom = (seed: number) => {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  };

  const randomness = 0.7 + seededRandom(index) * 0.3;
  const r1 = seededRandom(index * 2);
  const r2 = seededRandom(index * 3);
  const r3 = seededRandom(index * 5);

  const bqiScore = Math.round((70 + distanceFactor * 25 + r1 * 5) * 10) / 10;
  const speciesCount = Math.round(15 + distanceFactor * 35 + r2 * 10);
  const diversityIndex = Math.round((0.6 + distanceFactor * 0.3 + r3 * 0.1) * 100) / 100;
  const vegetationCoverage = Math.round((65 + distanceFactor * 30 + r1 * 5) * 10) / 10;
  const ndvi = Math.round((0.5 + distanceFactor * 0.4 + r2 * 0.1) * 100) / 100;
  const habitatQuality = Math.round((60 + distanceFactor * 35 + r3 * 5) * 10) / 10;
  const waterQuality = Math.round((70 + distanceFactor * 25 + r1 * 5) * 10) / 10;
  const soilHealth = Math.round((65 + distanceFactor * 30 + r2 * 5) * 10) / 10;
  const carbonStock = Math.round((80 + distanceFactor * 100 + r3 * 20) * 10) / 10;
  const creditValue = Math.round((carbonStock * 0.8 + bqiScore * 0.5) * randomness);

  return {
    bqiScore,
    speciesCount,
    diversityIndex,
    vegetationCoverage,
    ndvi,
    habitatQuality,
    waterQuality,
    soilHealth,
    carbonStock,
    creditValue,
    lastUpdated: new Date()
  };
}

/**
 * Gets color for BQI heatmap visualization
 */
export function getBQIColor(score: number): string {
  if (score >= 90) return '#006400'; // Dark green
  if (score >= 80) return '#228B22'; // Forest green
  if (score >= 70) return '#32CD32'; // Lime green
  if (score >= 60) return '#9ACD32'; // Yellow green
  if (score >= 50) return '#FFFF00'; // Yellow
  if (score >= 40) return '#FFA500'; // Orange
  return '#FF4500'; // Red orange
}

/**
 * Gets opacity for BQI heatmap
 */
export function getBQIOpacity(score: number): number {
  return 0.3 + (score / 100) * 0.4;
}

/**
 * Filter cells visible in current map viewport
 * PERFORMANCE: Only render cells within viewport bounds + small buffer
 */
export function filterCellsInViewport(
  cells: GridCell[],
  bounds: { north: number; south: number; east: number; west: number },
  bufferDegrees: number = 0.05
): GridCell[] {
  return cells.filter(cell => {
    const [lat, lng] = cell.coordinates;
    return (
      lat >= bounds.south - bufferDegrees &&
      lat <= bounds.north + bufferDegrees &&
      lng >= bounds.west - bufferDegrees &&
      lng <= bounds.east + bufferDegrees
    );
  });
}

/**
 * Cluster nearby cells based on zoom level
 * PERFORMANCE: Reduce number of rendered elements at lower zoom levels
 */
export function clusterCells(cells: GridCell[], zoomLevel: number): GridCell[] {
  // Show MORE cells at higher zoom levels for detail
  if (zoomLevel >= 13) {
    return cells; // Show ALL cells
  }

  if (zoomLevel >= 11) {
    return cells.filter((_, index) => index % 2 === 0); // Every 2nd cell
  }

  if (zoomLevel >= 9) {
    return cells.filter((_, index) => index % 4 === 0); // Every 4th cell
  }

  if (zoomLevel >= 7) {
    return cells.filter((_, index) => index % 8 === 0); // Every 8th cell
  }

  // At very low zoom, show very few cells
  return cells.filter((_, index) => index % 16 === 0); // Every 16th cell
}
